import 'mapbox-gl-leaflet'
import '@maplibre/maplibre-gl-leaflet'
import '@maptiler/leaflet-maptilersdk'

import L, { mapboxGL, tileLayer } from 'leaflet'

const baseTileOptions = {
    maxZoom: 20,
    minZoom: 2,
    traceBorderColor: null,
    traceColor: '#000',
}

const vectorLayerOptions = {
    interactive: false,
    updateInterval: 16,
}

const vectorMapRenderOptions = {
    crossSourceCollisions: false,
    fadeDuration: 0,
}

const createMapLibreMapLayer = (style, attribution) =>
    L.maplibreGL({
        ...vectorLayerOptions,
        ...vectorMapRenderOptions,
        style,
        attribution,
    })

const MAPBOX_ACCESS_TOKEN = process.env.VUE_APP_MAPBOX_TOKEN
const createMapboxLayer = style =>
    mapboxGL({
        accessToken: MAPBOX_ACCESS_TOKEN,
        style,
        attribution:
            '<a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noopener noreferrer">&copy; Mapbox</a> <a href="https://www.openstreetmap.org/about" target="_blank" rel="noopener noreferrer">&copy; OpenStreetMap</a>',
        ...vectorMapRenderOptions,
        ...vectorLayerOptions,
    })

const MAPTILER_API_KEY = process.env.VUE_APP_MAPTILER_KEY
const createMapTilerLayer = style =>
    new L.MaptilerLayer({
        apiKey: MAPTILER_API_KEY,
        style,
    })

const state = {
    addressCoordinates: null,
    forceAlign: false,
    heatmapData: null,
    lastUserPosition: null,
    mapInstance: null,
    navigationRoute: null,
    positionTrackingEnabled: false,
    savedBounds: null,
    tileProviders: [
        {
            id: 'standard',
            createLayer: () =>
                createMapLibreMapLayer(
                    'https://tiles.openfreemap.org/styles/bright',
                    '<a href="https://openfreemap.org/" target="_blank" rel="noopener noreferrer">OpenFreeMap</a> © <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener noreferrer">OpenMapTiles</a> Data from <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>'
                ),
            ...baseTileOptions,
            maxZoom: 23,
            traceColor: '#13365e',
        },
        {
            id: 'navigation',
            createLayer: () =>
                createMapboxLayer('mapbox://styles/mapbox/navigation-day-v1'),
            ...baseTileOptions,
            maxZoom: 23,
        },
        {
            id: 'hybrid',
            createLayer: () =>
                createMapTilerLayer(
                    'https://api.maptiler.com/maps/hybrid-v4/style.json'
                ),
            ...baseTileOptions,
            maxZoom: 20,
            traceBorderColor: '#000',
            traceColor: '#03fc07',
        },
        {
            id: 'swisstopo',
            createLayer: () =>
                tileLayer(
                    'https://wmts20.geo.admin.ch/1.0.0/ch.swisstopo.swissimage/default/current/3857/{z}/{x}/{y}.jpeg',
                    { maxZoom: 20, tileSize: 256 }
                ),
            ...baseTileOptions,
            traceBorderColor: '#000',
            traceColor: '#03fc07',
        },
        {
            id: 'swisstopo-hiking',
            createLayer: () =>
                tileLayer(
                    'https://wmts20.geo.admin.ch/1.0.0/ch.swisstopo.pixelkarte-farbe/default/current/3857/{z}/{x}/{y}.jpeg',
                    { maxZoom: 19, tileSize: 256 }
                ),
            ...baseTileOptions,
            maxZoom: 19,
            traceBorderColor: '#000',
            traceColor: '#03fc07',
        },
        {
            id: 'light',
            createLayer: () =>
                createMapboxLayer(
                    'mapbox://styles/mapbox/light-v10?optimize=true'
                ),
            ...baseTileOptions,
            maxZoom: 23,
            traceBorderColor: '#516677',
            traceColor: '#a4c7f5',
        },
        {
            id: 'dark',
            createLayer: () =>
                createMapboxLayer(
                    'mapbox://styles/mapbox/dark-v10?optimize=true'
                ),
            ...baseTileOptions,
            maxZoom: 23,
            traceBorderColor: '#516677',
            traceColor: '#a4c7f5',
        },
    ],
}

const getters = {
    activeTileProvider(state, getters, rootState, rootGetters) {
        const providerId =
            rootGetters['preferences/mapDisplayPreferences']
                .activeTileProviderId
        return (
            state.tileProviders.find(({ id }) => id === providerId) ||
            getters.defaultTileProvider
        )
    },
    defaultTileProvider(state) {
        return process.env.VUE_APP_DEFAULT_MAP
            ? state.tileProviders.find(
                  ({ id }) => id === process.env.VUE_APP_DEFAULT_MAP
              ) || state.tileProviders[0]
            : state.tileProviders[0]
    },
    liveUpdatesEnabledConditionally(state, getters, rootState, rootGetters) {
        return rootGetters['tracker/isTrackerCountHigh']
            ? rootGetters['preferences/mapDisplayPreferences']
                  .liveUpdatesEnabled
            : true
    },
    simpleMarkersEnabledConditionally(state, getters, rootState, rootGetters) {
        return rootGetters['tracker/isTrackerCountHigh']
            ? rootGetters['preferences/mapDisplayPreferences']
                  .simpleMarkersEnabled
            : false
    },
}

const actions = {}

const mutations = {
    addHeatmapData(state, data) {
        if (!state.heatmapData) {
            state.heatmapData = []
        }

        state.heatmapData.push(...data)
    },
    clearSavedBounds(state) {
        state.savedBounds = null
    },
    resetHeatmapData(state) {
        state.heatmapData = null
    },
    saveBounds(state, data) {
        state.savedBounds = data
    },
    setMapInstance(state, data) {
        state.mapInstance = data
    },
    setNavigationRoute(state, data) {
        state.navigationRoute = data
    },
    setPositionTrackingEnabled(state, data) {
        state.positionTrackingEnabled = data
    },
    setUserPosition(state, data) {
        state.lastUserPosition = data
    },
    setForceAlign(state, data) {
        state.forceAlign = data
    },
    setAddressCoordinates(state, data) {
        state.addressCoordinates = data
    },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
